import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { getCookie } from "@utils/utils";
import { sendErrorToServer } from "../../utils/jsErrorLogger";

const controller = new AbortController(); // Create an AbortController
const signal = controller.signal; // Get the signal

type ErrorData = {
  message: string;
  stack?: string;
  type: "axios.error" | "axiosFast.error";
  timestamp: string;
  requestUrl?: string;
  method?: string;
  status?: number;
};

const axiosInstance: AxiosInstance = axios.create({
  baseURL: process.env.API_URL,
  withCredentials: true,
  signal,
});

const axiosInstanceFast: AxiosInstance = axios.create({
  baseURL: process.env.FASTAPI_URL,
  withCredentials: true,
});

let redirectToLogin: (() => void) | null = null;

// Function to set the global redirection callback
export const setRedirectToLogin = (callback: () => void) => {
  redirectToLogin = callback;
};

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    (config as any).__instanceType = "axiosInstance";
    if (config.method === "post") {
      config.headers["Content-Type"] = "application/x-www-form-urlencoded";
      // config.headers['Content-Type'] = 'application/json';

      const csrfToken = getCookie("csrftoken");
      if (csrfToken) {
        config.headers["X-CSRFToken"] = csrfToken;
      }
    }
    // if development mode change the /static reqest to change the headers
    // if (process.env.NODE_ENV === "development") {
    //   if (config.url?.startsWith("/static")) {
    //     // update the header base url in  request to work with proxy setup
    //     config.baseURL = "http://" + process.env.SERVER_IP + ":5173";
    //   }
    // }

    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstanceFast.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    (config as any).__instanceType = "axiosInstanceFast";
    if (config.method === "post") {
      config.headers["Content-Type"] = "application/json";

      const csrfToken = getCookie("csrftoken");
      if (csrfToken) {
        config.headers["X-CSRFToken"] = csrfToken;
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => {
    if (
      response.headers["content-type"].includes("text/html") &&
      response.data &&
      typeof response.data === "string" &&
      response.data.includes("/login/?next=")
    ) {
      location.href = "/login/";
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401 && redirectToLogin) {
      redirectToLogin();
    }
    if (error.isAxiosError) {
      const errorData: ErrorData = {
        message: error.message,
        stack: error.stack,
        type: "axios.error",
        timestamp: new Date().toISOString(),
        requestUrl: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        status: error.response?.status,
      };
      sendErrorToServer(errorData);
    }
    return Promise.reject(error);
  },
);

axiosInstanceFast.interceptors.response.use(
  (response) => {
    if (
      response.headers["content-type"].includes("text/html") &&
      response.data &&
      typeof response.data === "string" &&
      response.data.includes("Authorisation Error")
    ) {
      location.href = "/login/";
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      if (redirectToLogin) {
        redirectToLogin();
      } else {
        location.href = "/login/";
      }
    }

    if (error.isAxiosError) {
      const errorData: ErrorData = {
        message: error.message,
        stack: error.stack,
        type: "axiosFast.error",
        timestamp: new Date().toISOString(),
        requestUrl: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        status: error.response?.status,
      };
      sendErrorToServer(errorData);
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
export { axiosInstanceFast };
